import React, { useState } from 'react';
import { Check, Crown, Sparkles, Building2, ArrowRight, Users, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/shared/PageTitle';
import subscriptionsData from '../../data/subscriptions.json';

const MySubscription: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<string>(subscriptionsData.currentSubscription.productId);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestingProduct, setRequestingProduct] = useState<string | null>(null);

  const currentProduct = subscriptionsData.products.find(p => p.id === subscriptionsData.currentSubscription.productId);

  const getProductIcon = (type: string) => {
    switch (type) {
      case 'design-partner':
        return <Sparkles className="w-6 h-6" />;
      case 'basic':
        return <Users className="w-6 h-6" />;
      case 'professional':
        return <Crown className="w-6 h-6" />;
      case 'enterprise':
        return <Building2 className="w-6 h-6" />;
      default:
        return <Users className="w-6 h-6" />;
    }
  };

  const getProductColor = (type: string) => {
    switch (type) {
      case 'design-partner':
        return 'from-purple-500 to-pink-500';
      case 'basic':
        return 'from-blue-500 to-cyan-500';
      case 'professional':
        return 'from-orange-500 to-red-500';
      case 'enterprise':
        return 'from-slate-700 to-slate-900';
      default:
        return 'from-gray-500 to-gray-700';
    }
  };

  const handleRequestChange = (productId: string) => {
    setRequestingProduct(productId);
    setShowRequestModal(true);
  };

  const handleConfirmRequest = () => {
    console.log('Request submitted for product:', requestingProduct);
    setShowRequestModal(false);
    setRequestingProduct(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <PageTitle title={t('subscription.pageTitle')} />
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('subscription.heading')}</h1>
            <p className="text-gray-600">{t('subscription.subtitle')}</p>
          </div>
          <button
            onClick={() => navigate('/billing-management')}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors shadow-md"
          >
            <CreditCard className="w-5 h-5" />
            <span>{t('subscription.manageBilling')}</span>
          </button>
        </div>

        {currentProduct && (
          <div className={`bg-gradient-to-r ${getProductColor(currentProduct.type)} rounded-2xl shadow-xl p-8 mb-8 text-white`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  {getProductIcon(currentProduct.type)}
                </div>
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h2 className="text-2xl font-bold">{currentProduct.name}</h2>
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold">
                      {t('subscription.currentPlan')}
                    </span>
                  </div>
                  <p className="text-white/90 mb-4">{currentProduct.description}</p>
                  <div className="flex items-center space-x-6">
                    <div>
                      <p className="text-white/70 text-sm">{t('subscription.licenseLimit')}</p>
                      <p className="text-2xl font-bold">
                        {currentProduct.licenseLimit === 'unlimited' ? '∞' : currentProduct.licenseLimit}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">{t('subscription.pricing')}</p>
                      <p className="text-2xl font-bold">
                        {currentProduct.pricing.isTrial ? (
                          <>
                            <span className="text-lg">{t('subscription.freeTrial')}</span>
                          </>
                        ) : (
                          <>{t('subscription.perYear', { amount: currentProduct.pricing.annual })}</>
                        )}
                      </p>
                    </div>
                    {currentProduct.pricing.isTrial && (
                      <div>
                        <p className="text-white/70 text-sm">{t('subscription.trialPeriod')}</p>
                        <p className="text-lg font-bold">{currentProduct.pricing.trialDuration}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('subscription.availablePlans')}</h2>
          <p className="text-gray-600">{t('subscription.availablePlansSubtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {subscriptionsData.products.map((product) => {
            const isCurrentPlan = product.id === subscriptionsData.currentSubscription.productId;

            return (
              <div
                key={product.id}
                className={`bg-white rounded-xl shadow-md border-2 transition-all ${
                  isCurrentPlan
                    ? 'border-green-500 ring-2 ring-green-200'
                    : selectedProduct === product.id
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => !isCurrentPlan && setSelectedProduct(product.id)}
              >
                <div className="p-6">
                  <div className={`w-12 h-12 bg-gradient-to-br ${getProductColor(product.type)} rounded-xl flex items-center justify-center text-white mb-4`}>
                    {getProductIcon(product.type)}
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{product.description}</p>

                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-baseline mb-2">
                      {product.pricing.isTrial ? (
                        <div>
                          <span className="text-2xl font-bold text-gray-900">{t('subscription.free')}</span>
                          <span className="text-sm text-gray-600 ml-2">{t('subscription.trial')}</span>
                        </div>
                      ) : (
                        <>
                          <span className="text-3xl font-bold text-gray-900">€{product.pricing.monthly}</span>
                          <span className="text-gray-600 ml-2">{t('subscription.perMonth')}</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {product.pricing.isTrial
                        ? t('subscription.trialAnnualCombo', { duration: product.pricing.trialDuration, annual: product.pricing.annual })
                        : t('subscription.annualBilled', { amount: product.pricing.annual })
                      }
                    </p>
                  </div>

                  <div className="mb-6">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-900">{product.licenseLimit}</span>
                    </div>
                  </div>

                  {isCurrentPlan ? (
                    <button
                      disabled
                      className="w-full px-4 py-3 bg-green-500 text-white rounded-lg font-semibold cursor-not-allowed opacity-80"
                    >
                      {t('subscription.currentPlanButton')}
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRequestChange(product.id);
                      }}
                      className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                    >
                      <span>{t('subscription.requestUpgrade')}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showRequestModal && requestingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('subscription.modal.heading')}</h3>
            <p className="text-gray-600 mb-6">
              {t('subscription.modal.body', { name: subscriptionsData.products.find(p => p.id === requestingProduct)?.name ?? '' })}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setRequestingProduct(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors"
              >
                {t('subscription.modal.cancel')}
              </button>
              <button
                onClick={handleConfirmRequest}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                {t('subscription.modal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubscription;
